import React, { useState } from "react";
import { OrchestrationStepState } from "../../../../types/orchestration";
import { formatDuration } from "../../../../utils/orchestrationLayout";
import { copyToClipboard } from "../../../../utils/clipboard";
import { STATUS_TAG_CLASS } from "./orchestrationStyles";
import StepStatusIcon from "./StepStatusIcon";

interface StepPopoverProps {
  step: OrchestrationStepState;
  executionNameFallback: string;
  left: number;
  top: number;
  onClose: () => void;
}

const Row: React.FC<{ k: string; children: React.ReactNode }> = ({ k, children }) => (
  <div className="flex justify-between gap-3 border-b border-gray-100 py-1.5 text-[12.5px] last:border-b-0 dark:border-gray-800">
    <span className="text-gray-500 dark:text-gray-400">{k}</span>
    <span className="text-right font-mono text-gray-900 dark:text-white">{children}</span>
  </div>
);

const StepPopover: React.FC<StepPopoverProps> = ({
  step,
  executionNameFallback,
  left,
  top,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);

  const execName = step.executionName || executionNameFallback;
  const fanTxt = step.fanOut
    ? `${step.fanOut.succeeded}/${step.fanOut.total}${
        step.fanOut.failed ? ` (${step.fanOut.failed} failed)` : ""
      }`
    : "n/a";
  const actual =
    step.durationMs != null
      ? formatDuration(step.durationMs)
      : step.status === "running"
      ? "in progress"
      : "—";

  const stepJson = JSON.stringify(
    {
      id: step.id,
      name: step.name,
      status: step.status,
      dependsOn: step.dependsOn,
      estimatedDurationLabel: step.estimatedDurationLabel,
      durationMs: step.durationMs,
      executionName: step.executionName,
      fanOut: step.fanOut,
      skipReason: step.skipReason,
      error: step.error,
    },
    null,
    2
  );

  return (
    <div
      role="dialog"
      aria-label={`${step.name} detail`}
      style={{ left, top, maxHeight: `calc(100vh - ${top}px - 16px)` }}
      onClick={(e) => e.stopPropagation()}
      className="fixed z-[70] flex w-[330px] flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-rp-separator dark:bg-rp-surface-raised"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-3 top-3 text-gray-400 hover:text-gray-900 dark:hover:text-white"
      >
        ✕
      </button>
      <h3 className="pr-6 text-[15px] font-bold text-gray-900 dark:text-white">
        {step.name}
      </h3>
      <div className="mt-0.5 break-all font-mono text-[11px] text-gray-400 dark:text-gray-500">
        {step.id}
      </div>

      <div className="-mr-2 min-h-0 flex-1 overflow-y-auto pr-2">
      <div className="mt-3">
        <Row k="Status">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TAG_CLASS[step.status]}`}
          >
            <StepStatusIcon status={step.status} size={12} />
            {step.status}
          </span>
        </Row>
        <Row k="Estimated">{step.estimatedDurationLabel || "—"}</Row>
        <Row k="Actual">{actual}</Row>
        <Row k="Fan-out">{fanTxt}</Row>
        <Row k="Execution">
          <span className="text-[10.5px]">{execName}</span>
        </Row>
        <div className="py-1.5 text-[12.5px]">
          <div className="text-gray-500 dark:text-gray-400">Depends on</div>
          <div className="mt-1 break-words font-mono text-[11px] text-gray-600 dark:text-gray-300">
            {step.dependsOn.length ? step.dependsOn.join(", ") : "none (root)"}
          </div>
        </div>
      </div>

      {step.error && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2.5 text-[11.5px] leading-relaxed text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <strong>Error</strong>
          <br />
          {step.error}
        </div>
      )}
      {step.skipReason && !step.error && (
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 text-[11.5px] italic text-gray-600 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300">
          Skipped: {step.skipReason}
        </div>
      )}
      </div>

      <div className="mt-3">
        <button
          type="button"
          onClick={() =>
            copyToClipboard(stepJson)
              .then(() => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1400);
              })
              .catch(() => undefined)
          }
          className={`w-full rounded-lg py-2 text-[12px] font-semibold text-white transition-colors ${
            copied ? "bg-green-600" : "bg-rp-amber hover:bg-yellow-600"
          }`}
        >
          {copied ? "Copied ✓" : "Copy step JSON"}
        </button>
      </div>
    </div>
  );
};

export default StepPopover;
